import { Parser } from "../../../Parser";
import { EventListOptions, MainGroupFunctions, SubGroupFunctions, DateRangeParam, Loader, HourSubheading, Mainheading, CardItem, FilterPredicate, Subheading } from "../../../typings";
import { observable, Lambda, observe, IObjectDidChange, computed, action } from "mobx";
import { createTransformer } from "mobx-utils";
import * as i from "../../../contracts";
import { DeviceDetect, EventsBySportIdRequest } from "sb-resp-lib";
import { MainGroupConfiguration, SubGroupConfiguration, CardType, ComponentType } from "../../../data";
import { getISODate, addHours, addDays, getEventTitle, getDailyHours, getDictPhrase, ComponentManager } from "../../../helpers";
import { StreamingFilterBlockConstant } from "../../../constants";

export class EventListPresenterState {
    public provider: Parser;
    public options: EventListOptions;
    private disposers: Lambda[];
    deviceDetect: DeviceDetect;
    @observable public sports: string[];
    @observable private dateRange?: DateRangeParam

    @observable mainGroupBy: MainGroupConfiguration
    @observable subGroupBy: SubGroupConfiguration
    @observable isFavoritesEnabled: boolean
    @observable isInFavoritesState: boolean;
    @observable isMiniEventCard: boolean;
    @observable loader: Loader;
    @observable showOddBtns: boolean;
    showLiveFixturesAndOutrights: boolean;
    componentManager: ComponentManager;
    EVENTS_CHUNK_SIZE = 20;

    constructor(provider: Parser, options: EventListOptions) {
        this.provider = provider;
        this.options = options;
        this.deviceDetect = DeviceDetect.getInstance();

        this.sports = provider.sports;
        this.dateRange = provider.dateRange;
        this.mainGroupBy = options.cms.MainGroupBy;
        this.subGroupBy = options.cms.SubGroupBy;
        this.isFavoritesEnabled = this.provider.isFavoritesEnabled;
        this.isInFavoritesState = this.provider.isInFavoritesState;
        this.isMiniEventCard = this.provider.isMiniEventCard;
        this.showLiveFixturesAndOutrights = options.cms.ShowLiveFixturesAndOutrights;
        this.loader = this.provider.loader;
        this.componentManager = new ComponentManager(this.showLeagueInEventCard, this.options.cms.NumberOfSelectionsToShow, this.deviceDetect)
        this.displaySubheadings = createTransformer(this.displaySubheadings.bind(this));
        this.displayLeagues = createTransformer(this.displayLeagues.bind(this));
        this.displayHours = createTransformer(this.displayHours.bind(this));
        this.displayEvents = createTransformer(this.displayEvents.bind(this));
        this.displayMarkets = createTransformer(this.displayMarkets.bind(this));
        this.displaySports = createTransformer(this.displaySports.bind(this));
        this.displayDays = createTransformer(this.displayDays.bind(this));
        this.displayAllEvents = createTransformer(this.displayAllEvents.bind(this));

        this.displayEventsBySportId = createTransformer(this.displayEventsBySportId.bind(this));
        this.displayEventsByLeague = createTransformer(this.displayEventsByLeague.bind(this));
        this.displayEventsByTime = createTransformer(this.displayEventsByTime.bind(this));
        this.displayEventsByDay = createTransformer(this.displayEventsByDay.bind(this));
        this.displayEventByMainGrouping = createTransformer(this.displayEventByMainGrouping.bind(this));
        this.displayFavoriteEventsByLeague = createTransformer(this.displayFavoriteEventsByLeague.bind(this));
        this.displayEventsById = createTransformer(this.displayEventsById.bind(this));
        this.shouldShowCustomSubheading = createTransformer(this.shouldShowCustomSubheading.bind(this));
        this.filterOutrightWithMarkets = this.filterOutrightWithMarkets.bind(this);

        this.disposers = [];

        this.showOddBtns = false;
    }

    shouldRenderSubheading(id: string) {
        return !this.displayEvents(id).length && !this.showFavoritesSection;
    }

    @computed get tooltipMessage() {
        return this.showOddBtns ? this.options.dict.HideAllOdds : this.options.dict.ShowAllOdds
    }

    @action.bound toggleOddsBtnOnClick() {
        this.showOddBtns = !this.showOddBtns;
        return this.showOddBtns;
    }

    activate() {
        this.provider.activate();
        this.attachObservable();
    }

    deactivate() {
        this.provider.deactivate();
        this.dettachObservable();
    }

    private attachObservable() {
        this.disposers = [
            observe(this.provider, this.updateProviderProps)
        ];
    }

    @action.bound updateProviderProps(change: IObjectDidChange) {
        let provider = change.object as Parser;
        this.sports = provider.sports;
        this.dateRange = provider.dateRange;
        this.isFavoritesEnabled = provider.isFavoritesEnabled;
        this.isInFavoritesState = provider.isInFavoritesState;
        this.isMiniEventCard = provider.isMiniEventCard;
        this.loader = provider.loader;
    }

    private dettachObservable() {
        this.disposers.map(x => typeof x === "function" && x());
        this.disposers = [];
    }

    @computed get isLoading() {
        return this.isGroupedByTime ? this.loader.isFirstLoad : this.loader.isLoading;
    }

    @computed get hasLoadEvents() {
        return !this.isLoading && this.provider.data.events.length > 0;
    }

    @computed get showLeagueInEventCard() {
        return this.options.cms.ShowLeagueInEventCard && this.mainGroupBy !== MainGroupConfiguration.League
            && this.subGroupBy !== SubGroupConfiguration.League;
    }

    addToSlipTranspiler(market: i.Market, selection: i.Selection) {
        let addOddData = this.provider.getAddOddData(market, selection);
        addOddData &&
            this.options.betSlipUtil.addOddToSlip(
                addOddData.masterEventID,
                addOddData.eventID,
                addOddData.lineID,
                addOddData.odds,
                addOddData.lineGroupID,
                addOddData.lineTypeID,
                addOddData.rowTypeID,
                addOddData.points,
                addOddData.isLive,
                addOddData.isQA,
                addOddData.eventTypeId,
                addOddData.leagueId,
                addOddData.betId,
                addOddData.preventUpdating,
                addOddData.isFavedTeam
            );
    }

    openGameTranspiler(event: i.Event, ev: Event) {
        ev && ev.stopPropagation();
        ev && ev.preventDefault();
        let openGameData = this.provider.getOpenGameData(event, ev);
        openGameData &&
            this.options.navHandler.OpenGame(
                openGameData.event,
                openGameData.id,
                openGameData.isLive,
                openGameData.evTypeForGameBlock,
                openGameData.shouldHideSidePanels,
                openGameData.branchID
            );
    }

    manageFavorites(event: i.Event, ev: Event) {
        ev && ev.stopPropagation();
        ev && ev.preventDefault();
        this.provider.manageFavorites(event);
    }

    openStatTranspiler(statisticsEventId: string, ev: Event) {
        ev && ev.preventDefault();
        ev && ev.stopPropagation();
        this.provider.openStats(statisticsEventId, ev);
    }

    openLiveStream(eventId: string, ev: Event) {
        if (this.options.dataProvider.isLiveStreamHandlerEnabled && !this.isMiniEventCard) {
            this.provider.openLiveStream(eventId);
            ev && ev.preventDefault();
            ev && ev.stopPropagation();
        }
    }

    @action.bound changeSubGrouping(newGrouping: SubGroupConfiguration) {
        this.subGroupBy = newGrouping;
    }

    openLeagueTranspiler(sportId: string, leagueId: string, ev: Event, element: HTMLElement) {
        ev && ev.stopPropagation();
        ev && ev.preventDefault();
        this.options.navHandler.OpenLeague(ev, leagueId, sportId, element);
    }

    openViewAllLiveLink(ev: Event) {
        ev && ev.stopPropagation();
        ev && ev.preventDefault();
        let sportId = this.sports.length ? this.sports[0] : 0;

        if (this.provider.isCurrentFilter(FilterPredicate.LiveStreaming)) {
            sportId = StreamingFilterBlockConstant;
        }
        this.options.navHandler.OpenGame(ev, 0, true, 0, 0, sportId);
    }

    getViewAllLiveLink() {
        return this.options.navHandler.Navigation.getLiveBettingLink();
    }

    // General display functions
    @computed get displayMainHeadings(): Mainheading[] {
        return this[MainGroupFunctions[this.mainGroupBy].headings](this.sports[0]);
    }

    public displaySubheadings(sportId?: string): Subheading[] {
        let subheadings = this[SubGroupFunctions[this.subGroupBy].headings](sportId);
        return subheadings.map((sub) => {
            sub.displayEvents = this.displayEvents(sub.id);
            return sub;
        }).filter((sub) => sub.displayEvents.length || this.showFavoritesSection);

    }

    public displayEvents(id: string): i.Event[] {
        const filteredEvents = this.shouldHideSubheadings ?
            this[MainGroupFunctions[this.mainGroupBy].events](id) :
            this[SubGroupFunctions[this.subGroupBy].events](id);
        return this.filterEventsByType(filteredEvents);
    }

    public displayMarkets(eventId: string): i.Market[] {
        return this.provider.data.markets.filter(x => x && x.eventId === eventId);
    }

    // Display functions depending on current grouping

    // Main headings and Subheadings
    public displaySports(id: string): Mainheading[] {
        let sportHeadings: Mainheading[] = [];
        this.sports.map((sportId) => {
            const event = this.provider.data.events.find((ev) => ev.sportId === sportId);
            event && sportHeadings.push({
                id: event.sportId,
                sportId: event.sportId,
                name: event.sportName
            });
        })
        return sportHeadings;
    }

    public displayLeagues(sportId: string): i.League[] {
        const currentSportId = sportId;
        return this.provider.data.leagues.filter((l) => l.sportId === currentSportId);
    }

    public displayHours(sportId?: string): HourSubheading[] {
        if (this.dateRange && this.dateRange.from) {
            const from = this.dateRange.from;
            const now = addHours(new Date(), this.provider.timeZone);
            const end = addDays(this.dateRange.from, 1);
            const isToday = from <= now && now <= end;
            if (isToday) {
                let hours = getDailyHours(now.getUTCHours());
                hours[0].name = this.options.dict.NextUp;
                return hours;
            }
        }
        return getDailyHours();
    }

    public displayDays(id?: string): Mainheading[] {
        let dates = {};
        this.provider.data.events.forEach((event) => {
            const evDate = getISODate(event.startEventDate, this.provider.timeZone);
            if (evDate) {
                const dateId = this.getDayId(evDate);
                dates[dateId] = {
                    id: dateId.toString(),
                    sportId: this.sports[0].toString(),
                    name: getDictPhrase(this.options.dict, `day-${evDate.getDay()}`),
                    subname: `${evDate.getDate()} ${getDictPhrase(this.options.dict, `month-${evDate.getMonth()}`)}`
                }
            }
        })
        return Object.keys(dates).map(key => dates[key]);
    }

    public displayAllEvents(id?: string): Mainheading[] {
        return this.provider.data.events.filter(
            (ev) =>
                this.displayMarkets(ev.id).length > 0)
            .map((ev) => {
                return {
                    id: ev.id,
                    sportId: ev.sportId,
                    name: getEventTitle(ev, this.displayMarkets(ev.id)[0], this.options.dict.EachWayTag)
                }
            });
    }

    // Events
    public displayEventsBySportId(sportId: string): i.Event[] {
        return this.provider.data.events.filter(event => event.sportId === sportId);
    }

    public displayEventsByLeague(leagueId: string): i.Event[] {
        let events = this.provider.data.events.filter(ev => ev.leagueId === leagueId);

        if (this.isInFavoritesState) {
            return this.filterFavoriteEvents(events, true);
        }

        if (this.showFavoritesSection) {
            return this.filterFavoriteEvents(events, false);
        }

        return events;
    }

    public displayEventsByTime(hour: string): i.Event[] {
        return this.provider.data.events.filter(ev => {
            const evDate = getISODate(ev.startEventDate, this.provider.timeZone);
            return evDate && evDate.getHours().toString() === hour;
        });
    }

    public displayEventsByDay(id: string): i.Event[] {
        return this.provider.data.events.filter(
            (event) => {
                const evDate = getISODate(event.startEventDate, this.provider.timeZone);
                const dateId = evDate && this.getDayId(evDate).toString();
                return dateId === id;
            }
        )
    }

    public displayEventByMainGrouping(id?: string): i.Event[] {
        const events = this[MainGroupFunctions[this.mainGroupBy].events](id);
        if (this.isInFavoritesState) {
            return this.filterFavoriteEvents(events, true);
        }

        return events;
    }

    public displayEventsById(id: string): i.Event[] {
        let event = this.provider.data.events.find((ev) => ev.id === id);
        return event ? [event] : [];
    }

    @computed get shouldHideSubheadings(): boolean {
        return this.subGroupBy === SubGroupConfiguration.None;
    }

    @computed get displayFavoriteEvents(): i.Event[] {
        return this.provider.data.events.filter((ev) => ev.isInFavorites);
    }

    public displayFavoriteEventsByLeague(id?: string): i.Event[] {
        return this.provider.data.events.filter((ev) => ev.isInFavorites && ev.leagueId === id);
    }

    @computed get displayFavoriteLeagues() {
        const favoriteLeagues = this.displayFavoriteEvents.reduce(
            (acc, ev) => {
                acc[ev.leagueId] = { id: ev.leagueId, name: ev.leagueName, sportId: ev.sportId }
                return acc;
            }, {}
        );
        return Object.keys(favoriteLeagues).map(key => favoriteLeagues[key]);
    }

    @computed get showFavoritesSection(): boolean {
        return this.isFavoritesEnabled
            && !this.isInFavoritesState
            && this.displayFavoriteEvents.length > 0
            && this.subGroupBy === SubGroupConfiguration.League;
    }

    private filterFavoriteEvents(events: i.Event[], showFavorites: boolean) {
        return events.filter((ev) => showFavorites ? ev.isInFavorites : !ev.isInFavorites);
    }

    @computed get shouldShowOutrightSubheading(): boolean {
        return this.mainGroupBy !== MainGroupConfiguration.Event || this.subGroupBy !== SubGroupConfiguration.None;
    }

    @computed get isGroupedByTime() {
        return this.subGroupBy === SubGroupConfiguration.Time;
    }

    getSportCardType(sportId: string): CardType {
        return this.getCardConfiguration(sportId).cardType;
    }

    getCardConfiguration(sportId: string): CardItem {
        const cardConfig = this.options.dataProvider.cardConfiguration[sportId] || this.options.dataProvider.cardConfiguration[0];
        const miniCardConfig = this.isMiniEventCard && (this.options.dataProvider.cardConfigurationEventView[sportId] || this.options.dataProvider.cardConfigurationEventView[0]);
        return miniCardConfig || cardConfig;
    }

    getMarketTemplates(sportId: string) {
        let cardItem = this.getCardConfiguration(sportId);
        let templates = cardItem.markets.map(m => m.template);
        return templates;
    }

    generateSubheadingId(mainHeadingId: string, subheadingId: string) {
        const dateId = (this.dateRange && this.dateRange.from) ? `${this.dateRange.from.getDate()}-` : "";
        return `${mainHeadingId}-${dateId}${subheadingId}`;
    }

    public shouldShowCustomSubheading(mainheading: Mainheading): boolean {
        return this.deviceDetect.isDesktop
            && this.subGroupBy === SubGroupConfiguration.None
            && this.shouldShowNamesInHeading(mainheading)
            && this.displayEventByMainGrouping(mainheading.id).some((ev) => ev.type === EventsBySportIdRequest.EventTypesEnum.Fixture.toString());
    }

    public shouldShowNamesInHeading(mainheading: Mainheading, isMainHeading?: boolean): boolean {
        const cardType = this.getSportCardType(mainheading.sportId);
        const shouldShowForDevice = this.deviceDetect.isDesktop && !this.isMiniEventCard;
        const shouldShowForCard = isMainHeading ? cardType === CardType.Multiview : (cardType === CardType.American || cardType === CardType.Multiview);

        return shouldShowForDevice && shouldShowForCard;
    }

    private filterEventsByType(events: i.Event[]) {
        const eventsWithMarkets = events.filter(this.filterOutrightWithMarkets);
        if (this.showLiveFixturesAndOutrights) {
            return eventsWithMarkets;
        }

        const fixtureEvents = eventsWithMarkets.filter(this.isFixture);
        return fixtureEvents.length ? fixtureEvents : eventsWithMarkets;
    }

    private isFixture(ev: i.Event): boolean {
        return ev.type !== EventsBySportIdRequest.EventTypesEnum.Outright.toString();
    }

    private filterOutrightWithMarkets(ev: i.Event): boolean {
        return ev.type === EventsBySportIdRequest.EventTypesEnum.Outright.toString() ? this.displayMarkets(ev.id).length > 0 : true;
    }

    getEventsHeight(events: i.Event[]): number[] {
        let eventChunkHeights: number[] = [];
        const eventToShow = Math.min(events.length, this.options.cms.ExpandedEvents);

        for (let current = 0; current < eventToShow;) {
            let numberOfEvents = Math.min(eventToShow - current, this.EVENTS_CHUNK_SIZE);
            let baseMinHeight = 0;

            for (let i = 0; i < numberOfEvents; i++) {
                let markets = (events[current + i].componentType === ComponentType.PreLiveOutright) ? this.displayMarkets(events[current + i].id) : [];
                baseMinHeight += this.componentManager.getComponentHeight(events[current + i], markets);
            }

            eventChunkHeights.push(baseMinHeight);
            let max = current + this.EVENTS_CHUNK_SIZE;
            current = Math.min(max, eventToShow);
        }

        return eventChunkHeights
    }

    private getDayId(evDate: Date) {
        return evDate.getDate() + evDate.getMonth() * 100 + evDate.getFullYear();
    }
}